import { useFormik } from "formik";
import React, { useImperativeHandle, useMemo } from "react";
import { View } from "react-native";
import tw from "twrnc";
import { sendPinnedMessage } from "@/components/http/events";
import { GuestbookMessageSchema } from "@/components/screens/Schemas";
import { notify } from "@/components/shared/AppToast";
import Button, { ButtonRow } from "@/components/settings/Button";
import { TextField } from "@/components/settings/Field";
import { t } from "@/localization";

// Posting a pinned message is an action (guests see it right away), so it has
// its own button instead of auto-save.
const GuestbookPinnedMessageForm = ({ event }, ref) => {
  const initialValues = {
    message: "",
    notifyGuests: false,
  };

  const f = useFormik({
    initialValues,
    validationSchema: GuestbookMessageSchema,
    onSubmit: async (values, actions) => {
      actions.setSubmitting(true);

      try {
        await sendPinnedMessage(event, values.message);
        actions.resetForm(initialValues);
        actions.setSubmitting(false);
        notify.success(
          t("Message pinned"),
          t("Guests see it at the top of the guestbook.")
        );
      } catch (err) {
        actions.setSubmitting(false);
        notify.error(
          t("Couldn't pin the message"),
          String(err?.message || err)
        );
      }
    },
  });

  useImperativeHandle(ref, () => ({
    isReadyToSave: () => {
      return !!f.dirty && !f.isSubmitting;
    },
  }));

  const updateMessage = useMemo(() => f.handleChange("message"), []);

  return (
    <View>
      <TextField
        multiline
        rows={4}
        value={f.values.message}
        onChangeText={updateMessage}
        onBlur={f.handleBlur("message")}
        placeholder={t("e.g. Welcome! The ceremony starts at 3 pm.")}
        accessibilityLabel={t("Pinned message")}
        hint={t("Stays at the top of the guestbook or chat.")}
        error={
          f.errors.message && f.touched.message && f.submitCount
            ? f.errors.message
            : null
        }
        last
      />
      <ButtonRow style={tw`mt-3`}>
        <Button
          text={t("Pin message")}
          icon="pin-outline"
          variant="primary"
          size="md"
          fullWidth={false}
          loading={f.isSubmitting}
          disabled={!f.values.message.trim()}
          onPress={f.handleSubmit}
        />
      </ButtonRow>
    </View>
  );
};

export default React.forwardRef(GuestbookPinnedMessageForm);
